import React from 'react';
import {
  NavLink as ReactRouterNavLink,
  NavLinkProps as ReactRouterNavLinkProps,
} from 'react-router-dom';
import { cn, capitalize } from '@express-labs/raven-tools';
import {
  useHandleOnClickWithIntercept,
  useHandleOnKeyDownWithIntercept,
} from '../helpers/hook';
import s from './NavButton.scss';

export type NavButtonProps = ReactRouterNavLinkProps & {
  /* Gives the link a "disabled" appearance however, unlike the html attribute "disabled", users are still able to tab-into a isAccessiblyDisabled Link and click on it. Any onClick or onKeyDown events while isAccessiblyDisabled is true are captured and ignored. */
  isAccessiblyDisabled?: boolean;
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (ev: React.KeyboardEvent<HTMLElement>) => void;
  /** Any additional class or classes to further customize the button appearance. */
  className?: string;
  /** Show the button as a solid button or as a hollow "ghost" button */
  mode?: 'solid' | 'ghost';
  /** Buttons come in three sizes */
  size?: 'mega' | 'large' | 'small';
  /** The color theme for the button. */
  theme?: 'light' | 'dark';
  /** Child content and/or JSX to render inside the HTML tags. */
};

const NavButton = React.forwardRef<HTMLAnchorElement, NavButtonProps>(
  function NavButton(
    {
      children,
      className,
      isAccessiblyDisabled = false,
      mode = 'solid',
      onClick,
      onKeyDown,
      size = 'large',
      theme = 'dark',
      to,
      ...restProps
    },
    ref
  ) {
    const handleOnClick = useHandleOnClickWithIntercept(
      isAccessiblyDisabled,
      onClick
    );

    const handleOnKeyDown = useHandleOnKeyDownWithIntercept(
      isAccessiblyDisabled,
      onKeyDown
    );

    // Example: 'solidDark', 'ghostDark', 'solidLight', 'ghostLight' 👻
    const classNameThemeMode = `${mode}${capitalize(theme)}`;

    return (
      <ReactRouterNavLink
        aria-disabled={isAccessiblyDisabled}
        className={cn(
          'rvn-ui--nav-link',
          s.btn,
          s[classNameThemeMode],
          s[size],
          isAccessiblyDisabled && s.inactive,
          className
        )}
        onClick={handleOnClick}
        onKeyDown={handleOnKeyDown}
        ref={ref}
        to={to}
        {...restProps}
      >
        {children}
      </ReactRouterNavLink>
    );
  }
);

NavButton.displayName = 'NavButton';

export default NavButton;
